import React, { useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, Tooltip, Brush,
  ReferenceLine, ReferenceArea, Customized,
} from "recharts";
import { TrendingUp, Minus, SquareDashedBottom, MousePointer2, Trash2, ZoomOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { num, usd } from "../lib/format";
import { timeAxis, priceAxisDecimals, axisWidth } from "../lib/chartAxis";
import useIsMobile from "../lib/useIsMobile";
import useZoomPan from "../lib/useZoomPan";

const TOOLS = [
  { value: "none", key: "select", icon: MousePointer2 },
  { value: "trend", key: "trendLine", icon: TrendingUp },
  { value: "hline", key: "level", icon: Minus },
  { value: "zone", key: "zone", icon: SquareDashedBottom },
];

/**
 * Interactive price chart with drawing tools.
 *
 * Two modes:
 *   - free (default)  the drawings live in local state and vanish on unmount
 *   - controlled      `shapes` + `onShapesChange` are supplied by the parent, which
 *                     is how a post captures what the investor drew
 *
 * `readOnly` renders the same picture with the toolbar and every handler removed.
 * That is what a post embed uses: a reader sees exactly the marked block the author
 * pointed at, and there is no code path by which they could move it.
 */
export default function AdvancedChart({
  data, isCrypto = false, readOnly = false, intraday = false,
  shapes: shapesProp, onShapesChange,
  range: rangeProp, onRangeChange,
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const zoom = useZoomPan(data || []);
  const [tool, setTool] = useState("none");
  const [pending, setPending] = useState(null);
  const [ownShapes, setOwnShapes] = useState([]);

  const controlled = Array.isArray(shapesProp);
  const shapes = controlled ? shapesProp : ownShapes;
  const setShapes = (next) => {
    const value = typeof next === "function" ? next(shapes) : next;
    if (controlled) onShapesChange?.(value); else setOwnShapes(value);
  };
  // A frozen chart is pinned to the window the author left it on; nothing else can
  // move it, so useZoomPan's live state is bypassed entirely.
  const range = rangeProp || zoom.range;
  const setRange = (r) => { onRangeChange ? onRangeChange(r) : zoom.setRange(r); };
  const { containerRef, reset, zoomed } = zoom;

  if (!data || data.length === 0) {
    return <div className="h-[280px] sm:h-[420px] flex items-center justify-center text-slate-400 text-sm">{t("instrument.noPriceHistory")}</div>;
  }

  // Y domain tracks only the currently zoomed-in window (driven by wheel/pinch
  // or by dragging the brush handles) so price scale rescales like TradingView.
  const windowed = data.slice(range.start, range.end + 1);
  // With the brush gone there is nothing to scroll a frozen chart back into view, so
  // the saved window becomes the whole series rather than a highlighted part of it.
  const plotted = readOnly ? windowed : data;
  const min = Math.min(...windowed.map((d) => d.low ?? d.close));
  const max = Math.max(...windowed.map((d) => d.high ?? d.close));
  const pad = (max - min) * 0.08 || 1;
  // Intraday candles (1D/1W/1M) are labelled with times; see lib/chartAxis.
  const time = timeAxis(windowed, intraday);
  const yDec = priceAxisDecimals(min - pad, max + pad);
  // Crypto's 9-decimal formatting is for sub-cent coins; on Bitcoin's axis it printed
  // 78.905,123456789 into a 38px column. Only a sub-1 price needs it on the axis.
  const axisCrypto = isCrypto && max < 1;
  const yFmt = (v) => num(v, yDec, { crypto: axisCrypto });
  const yWidth = axisWidth([yFmt(min - pad), yFmt(max + pad)], isMobile ? 10 : 11, isMobile ? 38 : 52);

  const handleClick = (e) => {
    if (readOnly || !e || tool === "none" || e.activeLabel == null) return;
    const date = e.activeLabel;
    const close = e.activePayload?.[0]?.payload?.close;
    if (close == null) return;
    if (tool === "hline") { setShapes((s) => [...s, { type: "hline", y: close }]); return; }
    if (!pending) { setPending({ date, close }); return; }
    if (tool === "trend") setShapes((s) => [...s, { type: "trend", x1: pending.date, y1: pending.close, x2: date, y2: close }]);
    else if (tool === "zone") setShapes((s) => [...s, { type: "zone", x1: pending.date, x2: date }]);
    setPending(null);
  };

  // Custom SVG layer for arbitrary trend lines (needs axis scales).
  const TrendLayer = (props) => {
    const xMap = props.xAxisMap && Object.values(props.xAxisMap)[0];
    const yMap = props.yAxisMap && Object.values(props.yAxisMap)[0];
    if (!xMap || !yMap) return null;
    const xs = xMap.scale, ys = yMap.scale;
    const half = (xs.bandwidth ? xs.bandwidth() : 0) / 2;
    const px = (d) => (xs(d) ?? 0) + half;
    return (
      <g>
        {shapes.filter((s) => s.type === "trend").map((s, i) => {
          const x1 = px(s.x1), x2 = px(s.x2), y1 = ys(s.y1), y2 = ys(s.y2);
          const growth = ((s.y2 - s.y1) / s.y1) * 100;
          const upc = growth >= 0 ? "#059669" : "#e11d48";
          return (
            <g key={i}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={upc} strokeWidth={2} />
              <circle cx={x1} cy={y1} r={3} fill={upc} /><circle cx={x2} cy={y2} r={3} fill={upc} />
              <rect x={(x1 + x2) / 2 - 26} y={(y1 + y2) / 2 - 20} width={52} height={16} rx={4} fill={upc} />
              <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 8} textAnchor="middle" fontSize={10} fill="#fff">
                {growth >= 0 ? "+" : ""}{growth.toFixed(1)}%
              </text>
            </g>
          );
        })}
        {pending && (tool === "trend") && (
          <circle cx={px(pending.date)} cy={ys(pending.close)} r={4} fill="#0052FF" stroke="#fff" strokeWidth={1.5} />
        )}
      </g>
    );
  };

  const zoneFirst = pending && tool === "zone";

  return (
    <div>
      {!readOnly && (
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="inline-flex bg-slate-100 rounded-xl p-1 overflow-x-auto" data-testid="advanced-tools">
          {TOOLS.map((tl) => (
            <button key={tl.value} data-testid={`tool-${tl.value}`} onClick={() => { setTool(tl.value); setPending(null); }}
              title={t(`advancedChart.${tl.key}`)} aria-label={t(`advancedChart.${tl.key}`)}
              className={`flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5 text-sm font-medium rounded-lg whitespace-nowrap shrink-0 transition-colors ${tool === tl.value ? "bg-white text-brand-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              <tl.icon size={16} className="shrink-0" /> <span className="hidden sm:inline">{t(`advancedChart.${tl.key}`)}</span>
            </button>
          ))}
        </div>
        <button data-testid="clear-drawings" onClick={() => { setShapes([]); setPending(null); }}
          title={t("advancedChart.clear")} aria-label={t("advancedChart.clear")} className="btn-outline py-2 px-3 sm:px-4 text-sm shrink-0">
          <Trash2 size={15} /> <span className="hidden sm:inline">{t("advancedChart.clear")}</span>
        </button>
      </div>
      )}

      {readOnly ? null : tool !== "none" ? (
        <div className="mb-3 text-xs text-brand-600 bg-brand-50 rounded-lg px-3 py-2">
          {tool === "trend" && (zoneFirst ? "" : (pending ? t("advancedChart.trendFinish") : t("advancedChart.trendStart")))}
          {tool === "hline" && t("advancedChart.levelHint")}
          {tool === "zone" && (pending ? t("advancedChart.zoneFinish") : t("advancedChart.zoneStart"))}
          {" "}{t("advancedChart.dragToZoom")}
        </div>
      ) : (
        <div className="mb-3 text-xs text-slate-400">{t("instrument.zoomHint")}</div>
      )}

      <div ref={readOnly ? undefined : containerRef}
           className={`relative touch-pan-y ${readOnly ? "h-[240px] sm:h-[340px]" : "h-[280px] sm:h-[420px]"}`}>
        {!readOnly && zoomed && (
          <button onClick={reset} data-testid="advanced-chart-reset-zoom"
            className="absolute top-1 right-1 z-10 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-slate-600 border border-slate-200 shadow-sm hover:text-brand-600 hover:border-brand-200 transition-colors">
            <ZoomOut size={12} /> {t("instrument.resetZoom")}
          </button>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={plotted} margin={{ top: 10, right: isMobile ? 0 : 8, left: 0, bottom: 0 }} onClick={handleClick} style={{ cursor: tool === "none" ? "default" : "crosshair" }}>
            <defs>
              <linearGradient id="advFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0052FF" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#0052FF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" {...time.axis} tick={{ fontSize: isMobile ? 10 : 11, fill: "#94a3b8" }} minTickGap={isMobile ? 24 : 40} axisLine={false} tickLine={false} />
            <YAxis yAxisId="price" domain={[min - pad, max + pad]} tick={{ fontSize: isMobile ? 10 : 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={yWidth} tickFormatter={yFmt} />
            <Tooltip formatter={(v) => [usd(v, { crypto: isCrypto }), "Close"]} {...time.tooltip} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Area yAxisId="price" type="linear" dataKey="close" stroke="#0052FF" strokeWidth={2} fill="url(#advFill)" isAnimationActive={false} />

            {shapes.filter((s) => s.type === "hline").map((s, i) => (
              <ReferenceLine key={`h${i}`} yAxisId="price" y={s.y} stroke="#7c3aed" strokeDasharray="5 4" label={{ value: num(s.y, 2, { crypto: isCrypto }), position: "right", fill: "#7c3aed", fontSize: 10 }} />
            ))}
            {shapes.filter((s) => s.type === "zone").map((s, i) => (
              <ReferenceArea key={`z${i}`} yAxisId="price" x1={s.x1} x2={s.x2} fill="#0052FF" fillOpacity={0.08} stroke="#0052FF" strokeOpacity={0.25} />
            ))}

            <Customized component={TrendLayer} />
            {/* No brush when frozen: it is the one control that would let a reader
                re-frame the author's argument. */}
            {!readOnly && (
              <Brush dataKey="date" height={isMobile ? 22 : 26} stroke="#0052FF" travellerWidth={isMobile ? 12 : 8} tickFormatter={time.axis.tickFormatter}
                startIndex={range.start} endIndex={range.end}
                onChange={(r) => setRange({ start: r.startIndex, end: r.endIndex })} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
